import { produce } from 'immer';
import { AnyAction, combineReducers } from 'redux';
import storage from 'redux-persist/lib/storage';
import { COMPLETE_ONBOARDING_TASK, RESET_ONBOARDING_TASK } from '../action';
import {
  ADVANCE_ONBOARDING_STEP,
  COMPLETE_ONBOARDING,
  RESET_ONBOARDING,
  SET_ONBOARDING_STEP,
  SET_ONBOARDING_TEST_MODE,
  SKIP_ONBOARDING,
  START_ONBOARDING,
} from '../actions/onboardingActions';
import {
  ADD_BREATH_COUNT,
  ADD_GRATITUDE,
  ADD_PENDING_THOUGHT,
  ADD_PLACEHOLDER_THOUGHT,
  ADD_THOUGHT_SUCCESS,
  CREATE_SAGE_THOUGHT,
  DELETE_THOUGHT_FAILURE,
  DELETE_THOUGHT_START,
  DELETE_THOUGHT_SUCCESS,
  FAIL_PENDING_THOUGHT,
  FETCH_SENTIMENT_SCORE,
  FETCH_THOUGHT_NOUNS,
  FETCH_USER,
  GET_MONTHLY_GOLDEN_HOUR_STATS,
  GET_THOUGHT_COUNTS,
  GET_THOUGHT_NOUNS,
  GET_WEEKLY_GOLDEN_HOUR_STATS,
  MODIFY_TIMER_DURATION,
  REMOVE_THOUGHT,
  REMOVE_THOUGHT_EMOTIONS,
  RESOLVE_PENDING_THOUGHT,
  SELECT_THOUGHT_EMOTIONS,
  SET_AUTHENTICATED,
  SET_AUTHORIZED,
  SET_AUTHORIZED_STATE,
  SET_BREATH_COUNT,
  SET_COSMOS_LATEST_RESPONSE,
  SET_CURRENT_MONTH_THOUGHTS,
  SET_THOUGHTS_LOADING,
  SET_GRATITUDES,
  SET_PIN_TOKEN,
  SET_REPORT_METADATA,
  SET_SAGE_THOUGHTS,
  SET_SESSION_TOKEN,
  SET_USERS_LATEST_RESPONSE,
  UPDATE_SAGE_THOUGHT,
  UPDATE_THOUGHT_AGENT_RESPONSE,
  UPDATE_USERS_LATEST_RESPONSE_FROM_THOUGHT,
  USER_LOGOUT,
} from '../middleware';
import { initialState, RootState } from '../schema';

import { mindmapReducer } from './mindmapReducer';
import { vaultLockReducer } from './vaultLockReducer';
import { wellnessScoreReducer } from './wellnessScoreReducer';
import { wellnessCalendarReducer } from './wellnessCalendarReducer';
import insightsReducer from './insights';

// Config reducer
const configReducer = (state = initialState.config, action: AnyAction) => {
  switch (action.type) {
    case MODIFY_TIMER_DURATION:
      return { ...state, timerDuration: action.payload.duration };
    default:
      return state;
  }
};

// Auth reducer
const authReducer = (state = initialState.auth, action: AnyAction) => {
  switch (action.type) {
    case SET_SESSION_TOKEN:
      return { ...state, sessionToken: action.payload.token };
    case SET_PIN_TOKEN:
      return { ...state, pinToken: action.payload.token };
    case SET_AUTHORIZED:
      return { ...state, isAuthorized: action.payload.isAuthorized };
    case SET_AUTHORIZED_STATE:
      return { ...state, authorized_state: action.payload.state };
    case SET_AUTHENTICATED:
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
      };
    default:
      return state;
  }
};

// Pulse of Planet reducer
const pulseOfPlanetReducer = (state = initialState.pulseOfPlanet, action: AnyAction) => {
  switch (action.type) {
    case ADD_BREATH_COUNT:
      return {
        ...state,
        breath_count: state.breath_count + action.payload.count,
      };
    case SET_BREATH_COUNT:
      return { ...state, breath_count: action.payload.count };
    case ADD_GRATITUDE:
      return {
        ...state,
        gratitudes: [...state.gratitudes, action.payload.text],
      };
    case SET_GRATITUDES:
      return { ...state, gratitudes: action.payload.gratitudes };
    default:
      return state;
  }
};

// Sage Thoughts reducer
const sageThoughtsReducer = (state = initialState.sageThoughts, action: AnyAction) => {
  return produce(state, (draft) => {
    switch (action.type) {
      case CREATE_SAGE_THOUGHT:
        const uuid = action.payload.thought_uuid;
        draft[uuid] = action.payload.sage_thought;
        break;
      case SET_SAGE_THOUGHTS:
        if (action.payload?.sage_thoughts) {
          Object.assign(draft, action.payload.sage_thoughts);
        }
        break;
      case UPDATE_SAGE_THOUGHT:
        if (action.payload?.thought_uuid) {
          const { thought_uuid, reframed_thought } = action.payload;
          if (draft[thought_uuid]) {
            draft[thought_uuid].user_reframed_thought = reframed_thought;
          }
        }
        break;
    }
  });
};

// Thoughts reducer
const thoughtsReducer = (state = initialState.thoughts, action: AnyAction) => {
  switch (action.type) {
    case SET_CURRENT_MONTH_THOUGHTS:
      return { ...state, all_thoughts: action.payload.thoughts };
    case SET_THOUGHTS_LOADING:
      return { ...state, loading: action.payload.loading };
    case ADD_PLACEHOLDER_THOUGHT:
      return {
        ...state,
        all_thoughts: [...state.all_thoughts, action.payload.thought],
      };
    case SELECT_THOUGHT_EMOTIONS:
      return {
        ...state,
        all_thoughts: state.all_thoughts.map((thought) =>
          thought.uuid === action.payload.uuid
            ? {
                ...thought,
                user_selected_emotions: action.payload.user_selected_emotions,
                // sentiment: action.payload.sentiment,
              }
            : thought,
        ),
      };
    case REMOVE_THOUGHT_EMOTIONS:
      return {
        ...state,
        all_thoughts: state.all_thoughts.map((thought) =>
          thought.uuid === action.payload.uuid
            ? { ...thought, user_selected_emotions: [] }
            : thought,
        ),
      };
    case ADD_THOUGHT_SUCCESS:
      return {
        ...state,
        all_thoughts: state.all_thoughts.map((thought) =>
          thought.uuid === null ? action.payload.thought : thought,
        ),
      };
    case REMOVE_THOUGHT:
      return {
        ...state,
        all_thoughts: state.all_thoughts.filter((thought) => thought.uuid !== null),
      };
    case UPDATE_THOUGHT_AGENT_RESPONSE:
      return {
        ...state,
        all_thoughts: state.all_thoughts.map((thought) =>
          thought.uuid === action.payload.uuid
            ? {
                ...thought,
                agent_response: action.payload.agentResponse,
                guiding_questions: action.payload.guiding_questions,
                _error: action.payload.error,
              }
            : thought,
        ),
      };
    case ADD_PENDING_THOUGHT:
      return {
        ...state,
        all_thoughts: [
          ...state.all_thoughts,
          {
            uuid: action.payload.pendingId, // Use pendingId as temporary UUID
            thought: action.payload.message,
            tags: action.payload.tags,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            journal_prompt: action.payload.journal_prompt,
            guiding_question_parent_id: action.payload.parent_id,
            sentiment: '',
            computed_top_emotions: [],
            user_selected_emotions: [],
            reframed: '',
            classifications: { biases: [], patterns: [] },
            _isPending: true,
            _pendingId: action.payload.pendingId,
          },
        ],
      };
    case RESOLVE_PENDING_THOUGHT:
      return {
        ...state,
        all_thoughts: state.all_thoughts.map((thought) =>
          thought._pendingId === action.payload.pendingId
            ? {
                ...action.payload.thought,
                _isPending: false,
                _pendingId: action.payload.pendingId, // Keep reference to the original pending ID
              }
            : thought,
        ),
      };
    case FAIL_PENDING_THOUGHT:
      return {
        ...state,
        all_thoughts: state.all_thoughts.map((thought) =>
          thought._pendingId === action.payload.pendingId
            ? {
                ...thought,
                _error: action.payload.error, // Store error message
                _isPending: true, // Keep as pending to show error state
              }
            : thought,
        ),
      };
    case DELETE_THOUGHT_START:
      // Optimistically remove thought from UI
      return {
        ...state,
        all_thoughts: state.all_thoughts.filter(
          (thought) => thought.uuid !== action.payload.uuid,
        ),
      };
    case DELETE_THOUGHT_SUCCESS:
      // Already removed in START, nothing more to do
      return state;
    case DELETE_THOUGHT_FAILURE:
      // Restore the thought on failure
      console.error('Failed to delete thought:', action.payload.error);

      // Find where to re-insert the thought (maintain chronological order by created_at)
      const restoredThought = action.payload.thought;
      const thoughtsWithRestored = [...state.all_thoughts, restoredThought].sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      return {
        ...state,
        all_thoughts: thoughtsWithRestored,
      };
    default:
      return state;
  }
};

// User reducer
const userReducer = (state = initialState.user, action: AnyAction) => {
  switch (action.type) {
    case FETCH_USER:
      return action.payload.user;
    default:
      return state;
  }
};

// Stats reducer
const statsReducer = (state = initialState.stats, action: AnyAction) => {
  switch (action.type) {
    case FETCH_THOUGHT_NOUNS:
      return { ...state, nouns: action.payload.nouns };
    case FETCH_SENTIMENT_SCORE:
      return { ...state, sentimentScore: action.payload.score };
    case GET_WEEKLY_GOLDEN_HOUR_STATS:
      return {
        ...state,
        weeklyGoldenHour: [...action.payload.weeklyGoldenHour],
        weeklyFetched: true,
      };
    case GET_MONTHLY_GOLDEN_HOUR_STATS:
      return {
        ...state,
        monthlyGoldenHour: [...action.payload.monthlyGoldenHour],
        monthlyFetched: true,
      };
    case GET_THOUGHT_NOUNS:
      if (action.payload.type === 'Week') {
        return {
          ...state,
          weeklyThoughtNouns: {
            positive: action.payload.positiveThoughtNouns,
            neutral: action.payload.neutralThoughtNouns,
            negative: action.payload.negativeThoughtNouns,
          },
        };
      } else if (action.payload.type === 'Month') {
        return {
          ...state,
          monthlyThoughtNouns: {
            positive: action.payload.positiveThoughtNouns,
            neutral: action.payload.neutralThoughtNouns,
            negative: action.payload.negativeThoughtNouns,
          },
        };
      } else {
        return {
          ...state,
          allTimeThoughtNouns: {
            positive: action.payload.positiveThoughtNouns,
            neutral: action.payload.neutralThoughtNouns,
            negative: action.payload.negativeThoughtNouns,
          },
        };
      }
    case GET_THOUGHT_COUNTS:
      return {
        ...state,
        allTimeThoughtCounts: {
          positive: action.payload.numPositiveThoughts,
          neutral: action.payload.numNeutralThoughts,
          negative: action.payload.numNegativeThoughts,
        },
      };
    default:
      return state;
  }
};

// Report Metadata reducer
const reportMetadataReducer = (state = initialState.reportMetadata, action: AnyAction) => {
  switch (action.type) {
    case SET_REPORT_METADATA:
      return action.payload.metadata;
    default:
      return state;
  }
};

// Onboarding reducer
const onboardingReducer = (state = initialState.onboarding, action: AnyAction) => {
  switch (action.type) {
    case COMPLETE_ONBOARDING_TASK:
      const updatedTasks = {
        ...state.tasks,
        [action.payload]: true,
      };
      return {
        ...state,
        tasks: updatedTasks,
      };
    case RESET_ONBOARDING_TASK:
      const resetTasks = {
        ...state.tasks,
        [action.payload]: false,
      };
      return {
        ...state,
        tasks: resetTasks,
      };
    case START_ONBOARDING:
      return {
        ...state,
        isOnboardingActive: true,
        currentOnboardingStep: 0,
      };
    case ADVANCE_ONBOARDING_STEP:
      return {
        ...state,
        currentOnboardingStep:
          state.currentOnboardingStep !== null ? state.currentOnboardingStep + 1 : 0,
      };
    case SET_ONBOARDING_STEP:
      return {
        ...state,
        currentOnboardingStep: action.payload.step,
      };
    case SKIP_ONBOARDING:
      return {
        ...state,
        isOnboardingActive: false,
        currentOnboardingStep: null,
        hasCompletedOnboarding: true,
      };
    case COMPLETE_ONBOARDING:
      return {
        ...state,
        isOnboardingActive: false,
        currentOnboardingStep: null,
        hasCompletedOnboarding: true,
      };
    case RESET_ONBOARDING:
      return {
        ...state,
        isOnboardingActive: true,
        currentOnboardingStep: 0,
        hasCompletedOnboarding: false,
      };
    case SET_ONBOARDING_TEST_MODE:
      return {
        ...state,
        testMode: action.payload.enabled,
      };
    default:
      return state;
  }
};

// Chat reducer
const chatReducer = (state = initialState.chat, action: AnyAction) => {
  return produce(state, (draft) => {
    switch (action.type) {
      case SET_COSMOS_LATEST_RESPONSE:
        draft.cosmos_latest_response = action.payload.cosmosResponse;
        break;
      case SET_USERS_LATEST_RESPONSE:
        draft.users_latest_response = action.payload.userResponse;
        break;
      case UPDATE_USERS_LATEST_RESPONSE_FROM_THOUGHT:
        // This action will be handled by a thunk that dispatches SET_USERS_LATEST_RESPONSE
        break;
      default:
        break;
    }
  });
};

// Combine all reducers
const appReducer = combineReducers<RootState>({
  user: userReducer,
  auth: authReducer,
  config: configReducer,
  pulseOfPlanet: pulseOfPlanetReducer,
  thoughts: thoughtsReducer,
  sageThoughts: sageThoughtsReducer,
  stats: statsReducer,
  reportMetadata: reportMetadataReducer,
  onboarding: onboardingReducer,
  vaultLock: vaultLockReducer,
  chat: chatReducer,
  mindmap: mindmapReducer,
  wellnessScore: wellnessScoreReducer,
  wellnessCalendar: wellnessCalendarReducer,
  insights: insightsReducer,
});

// Root reducer with logout handling
export const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: AnyAction,
): ReturnType<typeof appReducer> => {
  if (action.type === USER_LOGOUT) {
    storage.removeItem('persist:root');
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};
